import { typedApi } from "@/api/typed-client";
import { useQuery } from "@tanstack/react-query";

// Local minimal error class — only used to discriminate 404s during EDA polling.
// Kept local because src/api/client.ts is being removed (Phase E.3).
class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export interface EdaResult {
  result_id: string;
  dataset_id: string;
  analyses: Record<string, unknown>;
}

export interface RunEdaParams {
  project_id: string;
  dataset_id: string;
  analyses: string[];
  label?: string;
  model_id?: string;
  revision_no?: number;
  time_budget?: number;
  max_rows?: number;
  degree?: number;
  top_k?: number;
  fdr_q?: number;
  bins?: number;
  sample_size?: number;
  n_folds?: number;
  disagreement_threshold?: number;
  minority_warn_ratio?: number;
}

export interface RunEdaResponse {
  job_id: string;
  result_id: string;
}

interface AsyncResultState {
  status: "idle" | "queued" | "running" | "done" | "failed";
  progress?: number;
  result: EdaResult | null;
  error: string | null;
}

interface JobStatusResponse {
  job_id: string;
  status: "queued" | "running" | "done" | "failed";
  progress?: number;
  error_message?: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Hooks
// ────────────────────────────────────────────────────────────────────────────

/**
 * Polls job status and fetches result when done.
 *
 * - Polls GET /v1/jobs/{jobId} every ~3s while status is "running"/"queued"
 * - 404 during poll → treat as "still computing" (Plan 53e semantics), don't error
 * - When status is "done", fetches GET /v1/results/{resultId}
 * - Job status "failed" → returns error message
 * - jobId null → returns "idle" state
 */
export function useAsyncResult(
  jobId: string | null,
  resultId: string | null,
  options?: { pollingInterval?: number },
): AsyncResultState {
  const jobQuery = useQuery({
    queryKey: ["job", jobId],
    queryFn: async () => {
      if (!jobId) throw new Error("No job ID");
      try {
        const { data, error, response } = await typedApi.GET("/v1/jobs/{job_id}", {
          params: { path: { job_id: jobId } },
        });
        if (response.status === 404) {
          // 404 during poll → treat as "still computing" (Plan 53e semantics)
          return { job_id: jobId, status: "running" as const };
        }
        if (error) throw new ApiError(response.status, "HttpError", JSON.stringify(error));
        // Justified: job GET response is typed as JobOut but local type has narrower status union
        return data as unknown as JobStatusResponse;
      } catch (err) {
        // Re-raise ApiError 404 as synthetic running state
        if (err instanceof ApiError && err.status === 404) {
          return { job_id: jobId, status: "running" as const };
        }
        throw err;
      }
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling when done or failed
      if (!data || data.status === "done" || data.status === "failed") {
        return false;
      }
      // Poll every 3 seconds (or custom interval for tests) while queued/running
      return options?.pollingInterval ?? 3000;
    },
  });

  const resultQuery = useQuery({
    queryKey: ["result", resultId],
    queryFn: async () => {
      if (!resultId) throw new Error("No result ID");
      const { data, error } = await typedApi.GET("/v1/results/{result_id}", {
        params: { path: { result_id: resultId } },
      });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: result response is {[key:string]:unknown} in openapi schema
      return data as unknown as EdaResult;
    },
    enabled: !!resultId && jobQuery.data?.status === "done",
  });

  // No job ID → idle state
  if (!jobId) {
    return {
      status: "idle",
      result: null,
      error: null,
    };
  }

  // Job failed
  if (jobQuery.data?.status === "failed") {
    return {
      status: "failed",
      result: null,
      error: jobQuery.data.error_message ?? "Job failed",
    };
  }

  // Job done → return result state
  if (jobQuery.data?.status === "done") {
    if (resultQuery.data) {
      return {
        status: "done",
        result: resultQuery.data,
        error: null,
      };
    }
    // Result not yet loaded (shouldn't happen often)
    if (resultQuery.isLoading) {
      return {
        status: "done",
        result: null,
        error: null,
      };
    }
    // Result fetch failed
    if (resultQuery.error) {
      return {
        status: "failed",
        result: null,
        error:
          resultQuery.error instanceof Error ? resultQuery.error.message : "Failed to load result",
      };
    }
  }

  // Job query error (non-404)
  if (jobQuery.error) {
    return {
      status: "failed",
      result: null,
      error: jobQuery.error instanceof Error ? jobQuery.error.message : "Failed to load job status",
    };
  }

  // Job in progress
  return {
    status: jobQuery.data?.status ?? "queued",
    progress: jobQuery.data?.progress,
    result: null,
    error: null,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Functions
// ────────────────────────────────────────────────────────────────────────────

/**
 * Submits an EDA job.
 * POST to /v1/jobs/eda with JSON body, returns {job_id, result_id}.
 */
export async function runEda(params: RunEdaParams): Promise<RunEdaResponse> {
  const { data, error } = await typedApi.POST("/v1/jobs/eda", {
    // Justified: RunEdaParams uses optional (?) but EdaRequest uses optional+null (? | null); widening needed
    body: params as never,
  });
  if (error) throw new Error(JSON.stringify(error));
  // Justified: EDA response is {[key:string]:string} in openapi schema, not typed as {job_id, result_id}
  return data as unknown as RunEdaResponse;
}
